/*
 * Copyright 2026 Sony Corporation
 */
import {
  CAMERA_MODELS,
  CameraAddressSchema,
  CURRENT_CREDENTIALS_VERSION,
  MAX_CAMERA_ADDRESSES,
  type CameraModel,
  type CreateDeviceRequest,
  type Device,
  type Platform,
  type UpdateDeviceRequest,
} from '@ips-jit/schema';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CameraLoginFields, initialLogins, loginsToSend, type LoginsDraft } from '../components/CameraLoginFields';
import { CARD_GRID, NewCard } from '../components/NewCard';
import { LinuxIcon, PLATFORM_LABEL, PlatformIcon, PlatformThumbnail, WindowsIcon } from '../components/platform';
import { RuntimeGuide } from '../components/RuntimeGuide';
import { useAuth } from '../contexts/auth-context';
import { bundleFilename, combineBundle, installHint } from '../services/agent-bundle';
import { startDownload } from '../services/download';

const REFRESH_MS = 15_000;
const SHOWN_MODELS = 2;

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

/** Saves a blob as a file. The link is let go of later: revoking it at once can cancel the download. */
const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  startDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** "10.0.0.2, cam.local" as a list, or the first problem with it. */
export const parseAddresses = (text: string): { addresses: string[] } | { error: string } => {
  const parts = text
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const addresses: string[] = [];
  for (const part of parts) {
    const parsed = CameraAddressSchema.safeParse(part);
    if (!parsed.success) {
      return { error: `"${part}": ${parsed.error.issues[0]?.message ?? 'not a camera address.'}` };
    }
    if (!addresses.includes(parsed.data)) {
      addresses.push(parsed.data);
    }
  }
  return addresses.length > MAX_CAMERA_ADDRESSES
    ? { error: `At most ${MAX_CAMERA_ADDRESSES} camera addresses.` }
    : { addresses };
};

/** What a person needs to know about a runtime's credentials, or nothing when they are current. */
const credentialsNote = (device: Device): string | undefined => {
  const version = device.credentials_version;
  if (version !== null && version >= CURRENT_CREDENTIALS_VERSION) {
    return undefined;
  }
  return version === null && device.last_seen === null
    ? 'Not installed yet: download the agent'
    : 'Old credentials: download the agent again';
};

/**
 * The one line a card says about the runtime now: a warning about its credentials when there is one, else what runs
 * and how its cameras are while online, else when it was last seen. The details go in the tooltip.
 */
export const runtimeSummary = (
  device: Device
): { text: string; tone: 'warning' | 'success' | 'muted'; detail: string } => {
  const detail = [
    device.hostname,
    device.ip,
    device.agent_version && `agent ${device.agent_version}`,
    device.credentials_version && `credentials v${device.credentials_version}`,
    device.last_seen && `last seen ${new Date(device.last_seen).toLocaleString()}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const note = credentialsNote(device);
  if (note) {
    return { text: note, tone: 'warning', detail };
  }
  if (device.status !== 'online') {
    const text = device.last_seen ? `Last seen ${new Date(device.last_seen).toLocaleString()}` : 'Never connected';
    return { text, tone: 'muted', detail };
  }
  const app = device.app
    ? device.app.state === 'running'
      ? `App v${device.app.version} running`
      : `App v${device.app.version} ${device.app.state}`
    : device.running_version
      ? `App v${device.running_version}`
      : 'No app yet';
  const connected = device.cameras.filter((camera) => camera.state === 'connected').length;
  const cameras = device.cameras.length > 0 ? ` · cameras ${connected}/${device.cameras.length}` : '';
  // Green only when everything works: the app runs and every camera it uses is connected.
  const healthy = device.app?.state === 'running' && connected === device.cameras.length;
  return { text: `${app}${cameras}`, tone: healthy ? 'success' : 'warning', detail };
};

const SUMMARY_COLOR = { warning: 'warning.main', success: 'success.light', muted: 'text.secondary' } as const;

interface Confirmation {
  title: string;
  text: string;
  confirm: string;
  danger?: boolean;
  action: () => void;
}

const CreateRuntimeDialog = ({
  open,
  busy,
  onClose,
  onCreate,
}: {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onCreate: (body: CreateDeviceRequest) => void;
}) => {
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<Platform>('windows');
  const [models, setModels] = useState<CameraModel[]>([]);
  const [addresses, setAddresses] = useState('');
  const [logins, setLogins] = useState<LoginsDraft>(() => initialLogins([]));
  const [problem, setProblem] = useState<string>();

  useEffect(() => {
    if (open) {
      setName('');
      setPlatform('windows');
      setModels([]);
      setAddresses('');
      setLogins(initialLogins([]));
      setProblem(undefined);
    }
  }, [open]);

  // Choosing a model whose cameras have [Access Authen.] opens the login fields.
  const chooseModels = (value: CameraModel[]) => {
    setModels(value);
    if (!logins.open && initialLogins(value).open) {
      setLogins({ ...logins, open: true });
    }
  };

  const parsedAddresses = parseAddresses(addresses);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if ('error' in parsedAddresses) {
      setProblem(parsedAddresses.error);
      return;
    }
    const sent = loginsToSend(logins, parsedAddresses.addresses);
    if ('error' in sent) {
      setProblem(sent.error);
      return;
    }
    const hasLogins = Object.keys(sent.camera_logins).length > 0;
    onCreate({
      name: name.trim(),
      platform,
      camera_models: models,
      camera_addresses: parsedAddresses.addresses,
      ...(hasLogins ? sent : {}),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>New runtime</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 80 } }}
              helperText="The PC, for example “Studio PC”."
              required
              autoFocus
            />
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" id="platform-label">
                Platform
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={platform}
                onChange={(_, value: Platform | null) => value && setPlatform(value)}
                aria-labelledby="platform-label"
              >
                <ToggleButton value="windows" sx={{ gap: 1, px: 3 }}>
                  <WindowsIcon /> Windows
                </ToggleButton>
                <ToggleButton value="linux" sx={{ gap: 1, px: 3 }}>
                  <LinuxIcon /> Linux
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={CAMERA_MODELS}
              value={models}
              onChange={(_, value) => chooseModels(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Camera models"
                  helperText="The cameras the apps of this runtime work with; the assistant builds for them."
                />
              )}
            />
            <TextField
              label="Camera IP addresses (optional)"
              value={addresses}
              onChange={(event) => setAddresses(event.target.value)}
              placeholder="192.168.1.10, 192.168.1.11"
              helperText="The first is the camera an app connects to by default. Without one, the app asks."
            />
            <CameraLoginFields
              draft={logins}
              onChange={setLogins}
              addresses={'addresses' in parsedAddresses ? parsedAddresses.addresses : []}
            />
            {problem && <Alert severity="error">{problem}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy || !name.trim() || models.length === 0}>
            Create runtime
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const AddressesDialog = ({
  device,
  busy,
  onClose,
  onSave,
}: {
  device?: Device;
  busy: boolean;
  onClose: () => void;
  onSave: (device: Device, body: UpdateDeviceRequest) => void;
}) => {
  const [text, setText] = useState('');
  const [logins, setLogins] = useState<LoginsDraft>(() => initialLogins([]));
  const [problem, setProblem] = useState<string>();

  // Only when another runtime is opened: the list is polled, and a new copy of the same one must not reset the form.
  useEffect(() => {
    setText(device?.camera_addresses.join(', ') ?? '');
    setLogins(initialLogins(device?.camera_models ?? [], device?.camera_logins));
    setProblem(undefined);
  }, [device?.device_id]);

  const parsed = parseAddresses(text);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if ('error' in parsed) {
      setProblem(parsed.error);
      return;
    }
    if (!device) {
      return;
    }
    const sent = loginsToSend(logins, parsed.addresses, device.camera_logins);
    if ('error' in sent) {
      setProblem(sent.error);
      return;
    }
    onSave(device, {
      camera_addresses: parsed.addresses,
      ...(Object.keys(sent.camera_logins).length > 0 ? sent : {}),
      ...(logins.forget.length > 0 ? { forget_fingerprints: logins.forget } : {}),
    });
  };

  return (
    <Dialog open={device !== undefined} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Cameras of “{device?.name}”</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Camera IP addresses"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="192.168.1.10, 192.168.1.11"
              helperText="Empty lets the app ask. The running app restarts to use them."
              autoFocus
            />
            <CameraLoginFields
              draft={logins}
              onChange={setLogins}
              addresses={'addresses' in parsed ? parsed.addresses : []}
              cameras={device?.cameras}
            />
            {problem && <Alert severity="error">{problem}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const RuntimeCard = ({
  device,
  busy,
  onDownload,
  onEditAddresses,
  onDelete,
}: {
  device: Device;
  busy: boolean;
  onDownload: (os: Platform) => void;
  onEditAddresses: () => void;
  onDelete: () => void;
}) => {
  const summary = runtimeSummary(device);
  const extra = device.camera_models.length - SHOWN_MODELS;
  const logins = Object.keys(device.camera_logins).length;
  const addresses = [
    device.camera_addresses.length > 0 ? device.camera_addresses.join(', ') : 'not set',
    logins > 0 ? (logins === 1 ? 'SSH login' : `${logins} SSH logins`) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Card
      variant="outlined"
      component="article"
      aria-label={`Runtime ${device.name}`}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <PlatformThumbnail platform={device.platform} />
      <CardContent sx={{ flex: 1, pb: 1 }}>
        {/* Every line is one line high, whatever it says, so all cards have the same height. */}
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }} noWrap title={device.name}>
              {device.name}
            </Typography>
            <Chip size="small" label={device.status} color={device.status === 'online' ? 'success' : 'default'} />
          </Stack>
          <Typography
            variant="body2"
            noWrap
            title={[summary.text, summary.detail].filter(Boolean).join('\n')}
            sx={{ color: SUMMARY_COLOR[summary.tone] }}
          >
            {summary.text}
          </Typography>
          <Stack
            direction="row"
            sx={{ flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', minHeight: 24 }}
            aria-label="Camera models"
          >
            {device.camera_models.slice(0, SHOWN_MODELS).map((model) => (
              <Chip key={model} size="small" variant="outlined" label={model} />
            ))}
            {extra > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`+${extra}`}
                title={device.camera_models.slice(SHOWN_MODELS).join(', ')}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={addresses}>
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Camera IPs:{' '}
              </Box>
              {addresses}
            </Typography>
            <Button
              size="small"
              disabled={busy}
              onClick={onEditAddresses}
              aria-label={`Edit camera IPs of ${device.name}`}
              sx={{ minWidth: 0 }}
            >
              Edit
            </Button>
          </Stack>
        </Stack>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<PlatformIcon platform={device.platform} />}
          disabled={busy}
          onClick={() => onDownload(device.platform)}
          aria-label={`Download the agent for ${device.name}`}
          title="The agent for this runtime's platform, with its install scripts and credentials"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Download agent
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" color="error" disabled={busy} onClick={onDelete}>
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};

/** The PCs that run the apps: a guide to setting one up, then a card per runtime after the one that makes a new one. */
export const Runtimes = () => {
  const { api } = useAuth();
  const [devices, setDevices] = useState<Device[]>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Device>();
  const [confirmation, setConfirmation] = useState<Confirmation>();
  // A link from a project's runtime chip ("Add login") opens that runtime's cameras at once.
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  useEffect(() => {
    const device = editId ? devices?.find((d) => d.device_id === editId) : undefined;
    if (device) {
      setEditing(device);
      setSearchParams({}, { replace: true });
    }
  }, [editId, devices, setSearchParams]);

  const load = useCallback(async () => {
    try {
      setDevices(await api.listDevices());
    } catch (caught) {
      setError(describe(caught, 'Could not load runtimes.'));
    }
  }, [api]);

  useEffect(() => {
    void load();
    // Presence arrives through IoT and is written to the record, so the page polls for it.
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await task();
    } catch (caught) {
      setError(describe(caught, 'The request failed.'));
    } finally {
      setBusy(false);
    }
  };

  const create = (body: Parameters<typeof api.createDevice>[0]) =>
    void run(async () => {
      const device = await api.createDevice(body);
      setCreating(false);
      setNotice(`Runtime "${device.name}" created. Download its agent for the ${PLATFORM_LABEL[device.platform]} PC.`);
      await load();
    });

  const fetchDownload = async (device: Device, os: Platform) => {
    // The package first: if it cannot be had, the credentials the PC has keep working.
    const { url } = await api.getAgentDownload(os);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Could not download the ${PLATFORM_LABEL[os]} agent (${response.status}).`);
    }
    const agentPackage = await response.blob();
    const credentials = await api.downloadCredentials(device.device_id, os);
    saveBlob(await combineBundle(agentPackage, credentials.blob), bundleFilename(device.name, os));
    setNotice(`Agent for "${device.name}" downloaded. ${installHint(os)}`);
    await load();
  };

  const download = (device: Device, os: Platform) => {
    if (device.credentials_version === null) {
      void run(() => fetchDownload(device, os));
      return;
    }
    setConfirmation({
      title: `Download the agent for "${device.name}" again?`,
      text: 'The download holds new credentials. An agent installed from an earlier download stops working until you install this one.',
      confirm: 'Download',
      action: () => void run(() => fetchDownload(device, os)),
    });
  };

  const saveAddresses = (device: Device, body: UpdateDeviceRequest) =>
    void run(async () => {
      await api.updateDevice(device.device_id, body);
      setEditing(undefined);
      setNotice(`Cameras of "${device.name}" saved; the running app restarts to use them.`);
      await load();
    });

  const remove = (device: Device) =>
    setConfirmation({
      title: `Delete "${device.name}"?`,
      text: 'Its agent stops working immediately, and its projects can no longer be built.',
      confirm: 'Delete',
      danger: true,
      action: () =>
        void run(async () => {
          await api.deleteDevice(device.device_id);
          await load();
        }),
    });

  const close = () => setConfirmation(undefined);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      <RuntimeGuide />

      {devices === undefined ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : (
        <>
          <Box sx={CARD_GRID}>
            <NewCard label="New runtime" onClick={() => setCreating(true)} />
            {devices.map((device) => (
              <RuntimeCard
                key={device.device_id}
                device={device}
                busy={busy}
                onDownload={(os) => download(device, os)}
                onEditAddresses={() => setEditing(device)}
                onDelete={() => remove(device)}
              />
            ))}
          </Box>
          {devices.length === 0 && (
            <Typography color="text.secondary">
              No runtimes yet. A runtime is a PC with the agent, the app it runs and the cameras the app talks to.
            </Typography>
          )}
        </>
      )}

      <CreateRuntimeDialog open={creating} busy={busy} onClose={() => setCreating(false)} onCreate={create} />
      <AddressesDialog device={editing} busy={busy} onClose={() => setEditing(undefined)} onSave={saveAddresses} />

      <Dialog open={confirmation !== undefined} onClose={close}>
        <DialogTitle>{confirmation?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmation?.text}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            color={confirmation?.danger ? 'error' : 'primary'}
            variant="contained"
            onClick={() => {
              const action = confirmation?.action;
              close();
              action?.();
            }}
          >
            {confirmation?.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
